import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Newspaper, Search, X } from 'lucide-react';
import { useTranslation } from "react-i18next";
import Header from '../../components/Header/Header';
import TitleBand from '../../components/TitleBand/TitleBand';
import CtaBand from '../../components/Home/CtaBand';
import ContactForm from '../../components/Forms/ContactForm';
import ChatWidget from '../../components/ChatWidget/ChatWidget';
import { useInView } from '../../util/useInView';
import BlogCard, { BlogPost, blogImageStyle } from './BlogCard';
import { useBlogTitles } from './BlogContent';
import { PageSeo } from '../../seo/Seo';
import './Blog.css';

const normalize = (text: string) => text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const yearOf = (post: BlogPost) => post.rawDate.slice(0, 4);

// Um ano do arquivo: coluna fixa com o ano vazado e quantos artigos, e os cards entrando em cascata.
function YearGroup({ year, items }: { year: string; items: BlogPost[] }) {
  const { t } = useTranslation();
  const [ref, state] = useInView<HTMLUListElement>(0.1);

  return (
    <div className="cz-blog-year">
      <div className="cz-blog-year__label">
        <h2 className="cz-outline-num cz-blog-year__num">{year}</h2>
        <span className="cz-blog-year__count">{t('blog.post_count', { count: items.length })}</span>
      </div>
      <ul ref={ref} className={`cz-blog-year__grid${state}`}>
        {items.map((post, i) => (
          <li key={post.id} data-reveal style={{ '--i': i } as React.CSSProperties}>
            <BlogCard blog={post} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// /blog — mesmo padrão das outras páginas internas: faixa do título com a grade, busca e filtro por ano logo
// abaixo, destaques (o mais recente grande e os dois seguintes ao lado) e o arquivo por ano em faixa clara.
export default function Blog() {
  const { t } = useTranslation();
  const posts = useBlogTitles();
  const [query, setQuery] = useState('');
  const [year, setYear] = useState<string | null>(null);
  const q = normalize(query.trim());
  const filtering = Boolean(q || year);
  const [lead, ...rest] = posts;
  const side = rest.slice(0, 2);

  const years = useMemo(() => Array.from(new Set(posts.map(yearOf))), [posts]);

  // Sem filtro, os destaques também continuam no arquivo, cada um no seu ano.
  const groups = useMemo(() => {
    const list = posts.filter((p) => (!q || normalize(p.titulo).includes(q)) && (!year || yearOf(p) === year));
    const byYear = new Map<string, BlogPost[]>();
    list.forEach((post) => byYear.set(yearOf(post), [...(byYear.get(yearOf(post)) ?? []), post]));
    return Array.from(byYear, ([y, items]) => ({ year: y, items }));
  }, [posts, q, year]);

  const clear = () => {
    setQuery('');
    setYear(null);
  };

  return (
    <div className="cz-page cz-scope cz-blog-page">
      <PageSeo page="blog" path="/blog" />
      <Header />
      <main>
        <TitleBand
          title={
            <>
              {t('blog.title_lead', 'Novidades da')} <span className="cz-blog-accent">Calriz</span>
            </>
          }
          icon={Newspaper}
          singleLine
        />

        <div className="cz-blog-tools">
          <div className="cz-blog-search">
            <Search size={18} className="cz-blog-search__icon" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('blog.search_placeholder', 'Buscar artigos')}
              aria-label={t('blog.search_label', 'Buscar artigos no blog')}
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} aria-label={t('blog.clear_search', 'Limpar busca')}>
                <X size={16} />
              </button>
            )}
          </div>
          <div className="cz-blog-years" role="group" aria-label={t('blog.filter_label', 'Filtrar por ano')}>
            <button
              type="button"
              className={`cz-blog-years__chip${year ? '' : ' is-active'}`}
              aria-pressed={!year}
              onClick={() => setYear(null)}
            >
              {t('blog.all_years', 'Todos')}
            </button>
            {years.map((y) => (
              <button
                key={y}
                type="button"
                className={`cz-blog-years__chip${year === y ? ' is-active' : ''}`}
                aria-pressed={year === y}
                onClick={() => setYear(year === y ? null : y)}
              >
                {y}
              </button>
            ))}
          </div>
        </div>

        {!filtering && lead && (
          <section className="cz-blog-section cz-blog-highlights" aria-labelledby="cz-blog-lead-title">
            <Link to={`/blog/${lead.id}`} className="cz-blog-lead">
              <div className="cz-blog-lead__media">
                <div className="cz-blog-lead__img" style={blogImageStyle(lead)} />
              </div>
              <div className="cz-blog-lead__body">
                <div className="cz-blog-lead__meta">
                  <span className="cz-blog-lead__tag">{t('blog.featured_tag', 'Mais recente')}</span>
                  <span>{lead.data}</span>
                  <span className="cz-blog-lead__time">
                    <Clock size={14} aria-hidden="true" />
                    {t('blog.reading_time', { count: lead.minutes, defaultValue: '{{count}} min de leitura' })}
                  </span>
                </div>
                <h2 className="cz-blog-lead__title" id="cz-blog-lead-title">{lead.titulo}</h2>
                {lead.excerpt && <p className="cz-blog-lead__excerpt">{lead.excerpt}</p>}
                <span className="cz-blog-lead__read">
                  {t('blog.read_article', 'Ler artigo')}
                  <span className="cz-blog-lead__circle" aria-hidden="true">
                    <ArrowRight size={18} />
                  </span>
                </span>
              </div>
            </Link>

            {side.length > 0 && (
              <div className="cz-blog-side">
                <span className="cz-eyebrow">{t('blog.also_recent', 'Também recentes')}</span>
                <ul className="cz-blog-side__list">
                  {side.map((post) => (
                    <li key={post.id}>
                      <BlogCard blog={post} compact />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        <section className="cz-blog-band" aria-live="polite">
          <div className="cz-blog-section cz-blog-archive">
            {groups.map((group) => (
              <YearGroup key={group.year} year={group.year} items={group.items} />
            ))}

            {groups.length === 0 && (
              <div className="cz-blog-empty">
                <span className="cz-shape cz-shape--drop cz-blog-empty__shape" aria-hidden="true" />
                <strong>{t('blog.empty_title', 'Nenhum artigo encontrado')}</strong>
                <button type="button" onClick={clear}>{t('blog.clear_filters', 'Limpar filtros')}</button>
              </div>
            )}
          </div>
        </section>

        <CtaBand />
        <ContactForm />
      </main>
      <ChatWidget />
    </div>
  );
}
